import { EditOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Button, Avatar, ConfigProvider } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../../types/Workspace';
import { SprintStatusLabel, SprintStatusType } from '../../UI/SprintStatusLabel';
import { Epic } from '../../../types/Epic';
import ChevronLeftSvg from '../../../assets/icons/chevron-left.svg?react';
import FlagPriority from '../../../assets/icons/flag-priority.svg?react';
import FlagPriorityWhite from '../../../assets/icons/FlagPriorityWhite.svg?react';
import { WorkspaceAccessResponse } from '../../../api/workspace';
import { getUserProfilePictureUrl } from '../../../common/userHelper';
import { useGetWorkspace } from '../../../serverState/workspace';
import { useGetTasks } from '../../../serverState/tasks';
import { groupBy } from 'lodash';
import {
  TaskStatus,
  TaskStatusGroup,
  TaskStatusGroupUtils,
  defaultTaskStatuses,
  sortTaskStatusesByOrder,
} from '../../../types/TaskStatus';
import StatusLabel, { StatusComponentType } from '../../UI/StatusLabel';
import {
  PriorityOptions,
  PriorityFlagPropsMap,
  flagWidth,
  TaskEstimateLongMapping,
} from '../../TaskTable/helpers/helper';
import { useEffect, useRef, useState } from 'react';
import { Input, Checkbox } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import Task from '../../../types/Task';
import { CustomDatePicker } from '../../UI/CustomDatePicker/CustomDatePicker';
import { TaskEstimateLabel, TaskEstimateComponentType } from '../../UI/TaskEstimateLabel';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { Column, ViewColumnUtils } from '../../../types/WorkspaceViewColumn';
import { getWorkspaceViewGroup } from '../../../common/viewUtils';
import { TaskType, TaskTypeArray } from '../../../types/TaskType';
import { TaskTypeLabel } from '../../UI/Dropdown/TaskTypeDropdown';
import { SpaceLabel } from '../../../types/TaskLabel';
import LabelChip, { LabelChipType } from '../../UI/LabelChip';
import colorTokens from '../../../constants/design_system/colors';
import { EpicLabel } from '../../UI/Dropdown/EpicDropdown';
import BaseDropdown from '../../UI/Dropdown/BaseDropdown';
import { sortLabelsBySelection } from './editFieldsHelpers';
import './EditFieldsDropdown.scss';

/**
 * Applied to every submenu popup in this menu so the height cap and the gap from
 * the parent panel stay scoped to the batch menu. See EditFieldsDropdown.scss.
 */
const SUBMENU_POPUP_CLASS = 'batch-edit-fields-submenu';

/**
 * Why the submenu overlapped the parent panel (#905): rc-menu aligns the popup
 * to the submenu *title's* edge, not the panel's. The menu has `padding: 0 4px`,
 * so the title stops 4px short of the panel edge and the popup opens 4px inside
 * it — the two panels read as one surface (measured: title right 763, panel
 * right 767, popup 763).
 *
 * So the offset is that 4px of menu padding, to clear the panel edge, plus 4px
 * of actual gap.
 *
 * This has to go through rc-menu's placement API rather than CSS: rc-trigger
 * measures the popup's final rect when computing `left`, so it absorbs both
 * `margin-left` and `translate` — measured both, the popup lands on the same
 * pixel either way. rc-menu merges this over its defaults, so only the
 * placements that need an offset are listed.
 */
const MENU_PADDING = 4;
const SUBMENU_GAP = MENU_PADDING + 4;
const SUBMENU_PLACEMENTS = {
  rightTop: { points: ['tl', 'tr'], overflow: { adjustX: 1, adjustY: 1 }, offset: [SUBMENU_GAP, 0] },
  rightBottom: { points: ['bl', 'br'], overflow: { adjustX: 1, adjustY: 1 }, offset: [SUBMENU_GAP, 0] },
  // Flipped placements, used when there is no room on the right.
  leftTop: { points: ['tr', 'tl'], overflow: { adjustX: 1, adjustY: 1 }, offset: [-SUBMENU_GAP, 0] },
  leftBottom: { points: ['br', 'bl'], overflow: { adjustX: 1, adjustY: 1 }, offset: [-SUBMENU_GAP, 0] },
};

interface EditFieldsDropdownProps {
  framework: WorkspaceFramework;
  epics: Epic[];
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  selectedItems: number[]; // Array of selected task IDs
  onSelected: (
    fieldType: EditField,
    field:
      | TaskType
      | SprintStatusType
      | Epic
      | WorkspaceAccessResponse
      | TaskStatus
      | SpaceLabel[]
      | (Date | undefined)
      | PriorityOptions
      | number,
    action?: 'add' | 'remove' // Add action parameter for labels
  ) => void;
}

export const EditFieldsDropdown: React.FC<EditFieldsDropdownProps> = ({
  framework,
  epics,
  usersWithAccessToSpace,
  selectedItems,
  onSelected,
}) => {
  const viewContext = useViewContext();
  const workspaceQuery = useGetWorkspace();
  const statusOptions: TaskStatus[] = sortTaskStatusesByOrder(
    (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
  );
  const labelOptions: SpaceLabel[] = (workspaceQuery.data?.Labels as SpaceLabel[]) || [];
  const tasksQuery = useGetTasks(Number(workspaceQuery.data?.id));

  const [labelSearchTerm, setLabelSearchTerm] = useState('');

  const group = getWorkspaceViewGroup(location.pathname);
  const viewColumns = viewContext?.view
    ? ViewColumnUtils.updateAndSortColumn(framework, viewContext?.view)
    : ViewColumnUtils.defaultColumns(framework, { group });

  // Precompute menu items
  const taskTypeItem = taskTypeMenuItem();
  const sprintStatusItem = sprintStatusMenuItem(framework);
  const epicsItem = epicsMenuItem(epics, selectedItems, tasksQuery.data || []);
  const ownersItem = ownersMenuItem(usersWithAccessToSpace, selectedItems, tasksQuery.data || []);
  const statusesItem = statusesMenuItem(statusOptions);
  const labelsItem = labelsMenuItem(
    labelOptions,
    selectedItems,
    tasksQuery.data || [],
    labelSearchTerm,
    setLabelSearchTerm,
    onSelected
  );
  const startDateItem = startDateMenuItem((date) => {
    onSelected(EditField.startDate, date);
  });
  const deadlineItem = deadlineMenuItem((date) => {
    onSelected(EditField.deadline, date);
  });
  const priorityItem = priorityMenuItem();
  const estimateItem = estimateMenuItem(
    workspaceQuery.data?.estimationType || WorkspaceEstimationType.storyPoints,
    selectedItems,
    tasksQuery.data || []
  );

  const menuItems: ItemType[] = [
    taskTypeItem,
    // Stage is always available in batch actions even when the sprintStatus column
    // is hidden from the view (as it is by default in Sprint/Kanban) — moving
    // completed tasks in bulk between stages is a primary use case.
    sprintStatusItem,
    ...viewColumns
      .filter((column) => column.selected)
      .map((column) => {
        switch (column.id) {
          case Column.epic:
            return epicsItem;
          case Column.assignee:
            return ownersItem;
          case Column.status:
            return statusesItem;
          case Column.label:
            return labelsItem;
          case Column.startDate:
            return startDateItem;
          case Column.deadline:
            return deadlineItem;
          case Column.priority:
            return priorityItem;
          case Column.estimate:
            return estimateItem;
          default:
            return [];
        }
      })
      .flat(),
  ];

  const onSelectOption = (field: EditField, key: string) => {
    switch (field) {
      case EditField.taskType:
        onSelected(field, key as TaskType);
        break;
      case EditField.sprintStatus:
        onSelected(field, key as SprintStatusType);
        break;
      case EditField.epic:
        const epic = epics.find((proj) => `${proj.id}` === key);
        if (epic) {
          onSelected(field, epic);
        }
        break;
      case EditField.owner:
        if (key === 'remove') {
          onSelected(field, 0, 'remove');
        } else {
          const owner = usersWithAccessToSpace.find((userAccess) => `${userAccess.userId}` === key);
          if (owner) {
            onSelected(field, owner);
          }
        }
        break;
      case EditField.status:
        const status = statusOptions.find((statusOption) => `${statusOption.id}` === key);
        if (status) {
          onSelected(field, status);
        }
        break;
      case EditField.label:
        const label = labelOptions.find((labelOption) => `${labelOption.id}` === key);
        if (label) {
          const selectedTasks = tasksQuery.data?.filter((task) => selectedItems.includes(task.id)) || [];

          // Calculate how many selected tasks have this label
          const tasksWithLabel = selectedTasks.filter((task) => task.TaskLabels?.some((tl) => tl.labelId === label.id));
          const taskCount = tasksWithLabel.length;
          const totalSelectedTasks = selectedTasks.length;

          // Determine the action based on current state
          const isAllSelected = taskCount === totalSelectedTasks && totalSelectedTasks > 0;

          if (isAllSelected) {
            // All tasks have this label, so remove it from all
            onSelected(field, [label], 'remove');
          } else {
            // Some or no tasks have this label, so add it to all
            onSelected(field, [label], 'add');
          }
        }
        break;
      case EditField.priority:
        onSelected(field, key as PriorityOptions);
        break;
      case EditField.estimate:
        if (key === 'remove') {
          onSelected(field, 0, 'remove');
        } else {
          onSelected(field, parseInt(key));
        }
        break;
      default:
        break;
    }
  };

  return (
    <BaseDropdown
      disableTransition
      menu={{
        items: menuItems,
        // Open each field's options on click, not hover — hovering across the list
        // used to pop every submenu open in turn. #905
        triggerSubMenuAction: 'click',
        builtinPlacements: SUBMENU_PLACEMENTS,
        onClick: (e) => {
          const keyArray = e.key.split('_');
          if (keyArray[0] && keyArray[1]) {
            // Skip label clicks as they're handled directly in the label component
            if (keyArray[0] === EditField.label) {
              return;
            }
            onSelectOption(keyArray[0] as EditField, keyArray[1]);
          }
        },
      }}
      trigger={['click']}
      placement="topLeft"
    >
      <Button
        size="large"
        className="rounded-lg border-solid border-neutral-300 items-center gap-2 flex text-neutral-900 text-base font-semibold"
      >
        <EditOutlined className="w-4 h-4" />
        Edit Fields
      </Button>
    </BaseDropdown>
  );
};

export enum EditField {
  taskType = 'taskType',
  sprintStatus = 'sprintStatus',
  stage = 'stage',
  epic = 'epic',
  owner = 'owner',
  status = 'status',
  label = 'label',
  startDate = 'startDate',
  deadline = 'deadline',
  priority = 'priority',
  estimate = 'estimate',
}

const taskTypeMenuItem = (): ItemType => {
  return {
    key: EditField.taskType,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Type
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: TaskTypeArray.filter((taskType) => taskType !== TaskType.epic).map((taskType) => {
      return {
        key: `${EditField.taskType}_${taskType}`,
        label: <TaskTypeLabel type={taskType} size="medium" />,
      };
    }),
  };
};

const sprintStatusMenuItem = (framework: WorkspaceFramework): ItemType => {
  return {
    key: EditField.sprintStatus,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight !w-full">
        Stage
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: [
      {
        key: `${EditField.sprintStatus}_triage`,
        label: <SprintStatusLabel sprintStatus="triage" component="dropdown" />,
      },
      { type: 'divider' as const },
      {
        key: `${EditField.sprintStatus}_none`,
        label: <SprintStatusLabel sprintStatus="none" component="dropdown" />,
      },
      ...(framework === WorkspaceFramework.scrum
        ? [
            {
              key: `${EditField.sprintStatus}_planning`,
              label: <SprintStatusLabel sprintStatus="planning" component="dropdown" />,
            },
            {
              key: `${EditField.sprintStatus}_sprint`,
              label: <SprintStatusLabel sprintStatus="sprint" component="dropdown" />,
            },
          ]
        : framework === WorkspaceFramework.kanban
          ? [
              {
                key: `${EditField.sprintStatus}_kanban`,
                label: <SprintStatusLabel sprintStatus="kanban" component="dropdown" />,
              },
            ]
          : []),
      { type: 'divider' as const },
      {
        key: `${EditField.sprintStatus}_archive`,
        label: <SprintStatusLabel sprintStatus="archive" component="dropdown" />,
      },
    ],
  };
};

const epicsMenuItem = (epics: Epic[], selectedItems: number[], tasks: Task[]): ItemType => {
  const selectedTasks = tasks.filter((task) => selectedItems.includes(task.id));
  const totalSelectedTasks = selectedTasks.length;

  return {
    key: EditField.epic,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Epic
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: epics.map((epic) => {
      // Calculate how many selected tasks currently have this epic
      const taskCount = selectedTasks.filter((task) => task.epicId === epic.id).length;
      const isAllSelected = taskCount === totalSelectedTasks && totalSelectedTasks > 0;
      const isPartiallySelected = taskCount > 0 && taskCount < totalSelectedTasks;

      return {
        key: `${EditField.epic}_${epic.id}`,
        label: (
          <div className="flex items-center gap-2 h-6">
            <ConfigProvider
              theme={{
                components: {
                  Checkbox: {
                    colorPrimary: colorTokens.blue[600],
                    colorPrimaryHover: colorTokens.blue[700],
                  },
                },
              }}
            >
              <Checkbox checked={isAllSelected} indeterminate={isPartiallySelected} />
            </ConfigProvider>
            <EpicLabel epic={epic} size="medium" />
          </div>
        ),
      };
    }),
  };
};

const ownersMenuItem = (
  usersWithAccessToSpace: WorkspaceAccessResponse[],
  selectedItems: number[],
  tasks: Task[]
): ItemType => {
  const selectedTasks = tasks.filter((task) => selectedItems.includes(task.id));
  const hasOwner = selectedTasks.some((task) => task.assignee != null);

  const children: ItemType[] = usersWithAccessToSpace.map((userAccess: WorkspaceAccessResponse) => {
    const userName = userAccess.User.fullName || userAccess.User.email;
    const profileImgUrl = getUserProfilePictureUrl(userAccess.User.profilePicturePath);
    return {
      key: `${EditField.owner}_${userAccess.userId}`,
      label: (
        <>
          <Avatar src={profileImgUrl} size="small" style={{ marginRight: '5px' }}>
            {userName.slice(0, 2)}
          </Avatar>
          {userName}
        </>
      ),
    };
  });

  if (hasOwner) {
    // Separate the destructive action from the list of people, the way the
    // label submenu already separates "Remove Labels". #2789
    children.push({ type: 'divider' });
    children.push({
      key: `${EditField.owner}_remove`,
      label: (
        <div className="flex items-center justify-start text-sm font-normal text-neutral-900">
          <CloseOutlined className="mr-1" />
          Remove owner
        </div>
      ),
    });
  }

  return {
    key: EditField.owner,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Owner
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children,
  };
};

const statusesMenuItem = (statuses: TaskStatus[]): ItemType => {
  const groupedStatuses = groupBy(statuses, 'group');
  return {
    key: EditField.status,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Status
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: Object.values(TaskStatusGroup).map((statusGroup) => ({
      key: `${EditField.status}_group_${statusGroup}`,
      type: 'group',
      label: TaskStatusGroupUtils.getTitle(statusGroup),
      children: (groupedStatuses[statusGroup] ?? []).map((status: TaskStatus) => {
        return {
          key: `${EditField.status}_${status.id}`,
          label: (
            <StatusLabel
              group={status.group}
              title={status.title}
              color={status.color}
              component={StatusComponentType.Dropdown}
            />
          ),
        };
      }),
    })),
  };
};

const labelsMenuItem = (
  labels: SpaceLabel[],
  selectedItems: number[],
  tasks: Task[],
  searchTerm: string,
  setSearchTerm: (term: string) => void,
  onSelected: (fieldType: EditField, field: SpaceLabel[], action?: 'add' | 'remove') => void
): ItemType => {
  // Get labels from selected tasks
  const selectedTasks = tasks.filter((task) => selectedItems.includes(task.id));
  const selectedTaskLabels = selectedTasks.flatMap((task) => task.TaskLabels || []);
  const selectedLabelIds = [...new Set(selectedTaskLabels.map((tl) => tl.labelId))];
  const filteredLabels = sortLabelsBySelection(
    labels.filter((label) => label.title.toLowerCase().includes(searchTerm.toLowerCase())),
    selectedTasks
  );

  const menuChildren: ItemType[] = [];

  // Search field
  menuChildren.push({
    key: 'search-field',
    disabled: true,
    label: (
      <div className="py-1" onClick={(e) => e.stopPropagation()}>
        <Input
          placeholder="Search for Label"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<SearchOutlined className="text-neutral-400" />}
          className="border-neutral-300 rounded-md"
          size="middle"
        />
      </div>
    ),
  });

  // Labels with checkboxes
  if (filteredLabels.length > 0) {
    filteredLabels.forEach((label: SpaceLabel) => {
      // Calculate how many selected tasks have this label
      const tasksWithLabel = selectedTasks.filter((task) => task.TaskLabels?.some((tl) => tl.labelId === label.id));
      const taskCount = tasksWithLabel.length;
      const totalSelectedTasks = selectedTasks.length;

      // Determine checkbox state
      const isAllSelected = taskCount === totalSelectedTasks && totalSelectedTasks > 0;
      const isPartiallySelected = taskCount > 0 && taskCount < totalSelectedTasks;

      menuChildren.push({
        key: `${EditField.label}_${label.id}`,
        label: (
          <div
            className="flex items-center gap-2 w-full"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              // Determine the action based on current state
              if (isAllSelected) {
                // All tasks have this label, so remove it from all
                onSelected(EditField.label, [label], 'remove');
              } else {
                // Some or no tasks have this label, so add it to all
                onSelected(EditField.label, [label], 'add');
              }
            }}
          >
            <ConfigProvider
              theme={{
                components: {
                  Checkbox: {
                    colorPrimary: colorTokens.blue[600],
                    colorPrimaryHover: colorTokens.blue[700],
                  },
                },
              }}
            >
              <Checkbox checked={isAllSelected} indeterminate={isPartiallySelected} />
            </ConfigProvider>
            <LabelChip label={label} type={LabelChipType.Dropdown} />
          </div>
        ),
      });
    });
  } else if (searchTerm && labels.length > 0) {
    menuChildren.push({
      key: 'no-results',
      disabled: true,
      label: <div className="text-neutral-500 text-sm px-3 py-2">No labels found for &quot;{searchTerm}&quot;</div>,
    });
  }

  // Remove Labels option at the bottom (only show if there are selected labels)
  if (selectedLabelIds.length > 0) {
    menuChildren.push({ type: 'divider' });
    menuChildren.push({
      key: 'remove-all-labels',
      label: (
        <div className="flex items-center gap-2 text-neutral-900">
          <CloseOutlined className="w-4 h-4" />
          <span>Remove Labels</span>
        </div>
      ),
      onClick: () => {
        const selectedLabels = labels.filter((label) => selectedLabelIds.includes(label.id));
        onSelected(EditField.label, selectedLabels, 'remove');
      },
    });
  }

  return {
    key: EditField.label,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Label
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: menuChildren,
  };
};

const startDateMenuItem = (onChange: (date?: Date) => void): ItemType => {
  return {
    key: EditField.startDate,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Start Date
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: [
      {
        key: 'startDate',
        label: (
          <BatchDatePickerItem
            placeholder="Set Start Date"
            headerMessage="Changes will be applied only where the Start Date is before the Target Date."
            onChange={onChange}
            mode="startDate"
          />
        ),
      },
    ],
  };
};

const deadlineMenuItem = (onChange: (date?: Date) => void): ItemType => {
  return {
    key: EditField.deadline,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Target Date
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: [
      {
        key: 'deadline',
        label: (
          <BatchDatePickerItem
            placeholder="Set Target Date"
            headerMessage="Changes will be applied only where the Target Date is after the Start Date."
            onChange={onChange}
            mode="targetDate"
          />
        ),
      },
    ],
  };
};

const priorityMenuItem = (): ItemType => {
  return {
    key: EditField.priority,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Priority
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children: (Object.keys(PriorityOptions) as (keyof typeof PriorityOptions)[]).reverse().map((option) => {
      const value = PriorityOptions[option];
      const { colorFill, label } = PriorityFlagPropsMap[value];
      return {
        key: `${EditField.priority}_${option}`,
        label: (
          <div className="flex items-center w-[120px]">
            {value === PriorityOptions.NoPriority ? (
              <FlagPriorityWhite style={{ width: flagWidth }} />
            ) : (
              <FlagPriority style={{ width: flagWidth }} fill={colorFill} />
            )}
            <span style={{ color: colorFill }}>{label}</span>
          </div>
        ),
      };
    }),
  };
};

const estimateMenuItem = (
  estimationType: WorkspaceEstimationType,
  selectedItems: number[],
  tasks: Task[]
): ItemType => {
  const selectedTasks = tasks.filter((task) => selectedItems.includes(task.id));
  const hasEstimate = selectedTasks.some((task) =>
    estimationType === WorkspaceEstimationType.tshirtSizes ? task.estimate != null : task.storyPoints != null
  );

  const children: ItemType[] = Object.keys(TaskEstimateLongMapping(estimationType)).map((keyStr) => {
    const key = Number(keyStr);
    return {
      key: `${EditField.estimate}_${key}`,
      label: (
        <div className="flex items-center justify-start">
          <TaskEstimateLabel
            estimate={key}
            component={TaskEstimateComponentType.dropdown}
            estimationType={estimationType}
          />
        </div>
      ),
    };
  });

  if (hasEstimate) {
    children.push({
      key: `${EditField.estimate}_remove`,
      label: (
        <div className="flex items-center justify-start text-sm font-normal text-neutral-900">
          <CloseOutlined className="mr-1" />
          Remove estimate
        </div>
      ),
    });
  }

  return {
    key: EditField.estimate,
    label: (
      <div className="flex justify-between text-neutral-900 text-sm font-normal leading-tight">
        Estimate
        <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="-mr-5" />
      </div>
    ),
    expandIcon: <></>,
    popupClassName: SUBMENU_POPUP_CLASS,
    children,
  };
};

/**
 * Opening the Start/Target Date field IS the request to pick a date, so the
 * calendar shows straight away instead of making the user click a "Set Date"
 * control inside the submenu first. #905
 *
 * The timing is the whole problem. `CustomDatePicker` opens itself when given no
 * `open` prop, but it mounts with the submenu popup — before that popup has been
 * positioned — so rc-trigger aligned the calendar against a rect that did not
 * exist yet and parked it off-screen (measured: x=-17264, opacity 0). So the
 * calendar is mounted only once this item is actually on screen, which is what
 * the observer waits for.
 *
 * State is kept inside this component on purpose: anything stored on
 * `EditFieldsDropdown` rebuilds `menuItems`, which resets rc-menu's internal open
 * keys and slams the submenu shut the moment it opens.
 */
const BatchDatePickerItem: React.FC<{
  placeholder: string;
  headerMessage: string;
  onChange: (date?: Date) => void;
  mode: 'startDate' | 'targetDate';
}> = ({ placeholder, headerMessage, onChange, mode }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting));
    observer.observe(anchor);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={anchorRef}>
      {onScreen && (
        <CustomDatePicker
          placeholder={placeholder}
          onChange={(date) => {
            onChange(date ? date.toDate() : undefined);
          }}
          onClose={() => undefined}
          mode={mode}
          showFooter={true}
          footerTitle={mode === 'startDate' ? 'Remove All Start Date(s)' : 'Remove All Target Date(s)'}
          extraHeader={
            <div className="flex justify-start items-start bg-neutral-200 rounded-lg p-2 m-1 gap-2">
              <InfoCircleFilled className="w-4 h-4 text-neutral-800" />
              <div className="w-[236px] text-neutral-900 text-xs font-normal leading-none">{headerMessage}</div>
            </div>
          }
        />
      )}
    </div>
  );
};
